import React, { useState, useMemo } from "react";
import { ArrowBack, ReceiptLongOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NoteSelect from "../../components/NoteSelect";
import backgroundPattern from "../../assets/pasbookpng.png";
import "./SalesOrder.css";
import "./ProformaInvoice.css";

const STORAGE_KEY = "proformaInvoices";
const today = () => new Date().toISOString().slice(0, 10);
const safeArray = (v) => (Array.isArray(v) ? v : []);
const money = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyItem = () => ({
  id: `${Date.now()}-${Math.random()}`,
  item: "",
  qty: 1,
  rate: 0,
  gst: 18,
});

export default function ProformaInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData || null;
  const quotationData = location.state?.quotationData || null;

  const getSourceRef = () => {
    if (quotationData) return { type: 'quotation', id: quotationData.id, number: quotationData.quotationNo || `EST-${String(quotationData.id || '').slice(-6)}` };
    if (editData?.sourceRef) return editData.sourceRef;
    return null;
  };

  const mapQuotationItems = () => {
    if (!quotationData?.cylinders?.length) return [emptyItem()];
    return quotationData.cylinders.map((row) => ({
      id: `${Date.now()}-${Math.random()}`,
      item: row.cylinderType || row.cylinderName || '',
      qty: Number(row.qty || 1),
      rate: Number(row.rate || 0),
      gst: Number(row.gst || 18),
    }));
  };

  const [piNo, setPiNo] = useState(editData?.piNo || `PI-${String(Date.now()).slice(-6)}`);
  const [date, setDate] = useState(editData?.date || today());
  const [customer, setCustomer] = useState(editData?.customer || quotationData?.customer?.name || "");
  const customerId = editData?.customerId || quotationData?.customerId || null;
  const [customerMobile, setCustomerMobile] = useState(editData?.customerMobile || quotationData?.customer?.mobile || "");
  const [customerEmail, setCustomerEmail] = useState(editData?.customerEmail || quotationData?.customer?.email || "");
  const [customerAddress, setCustomerAddress] = useState(editData?.customerAddress || quotationData?.customer?.address || "");
  const [notes, setNotes] = useState(editData?.notes || quotationData?.notes || "");
  const [terms, setTerms] = useState(editData?.terms || quotationData?.terms || "");
  const sourceRef = getSourceRef();
  const [items, setItems] = useState(safeArray(editData?.items).length ? editData.items : mapQuotationItems());
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    let subtotal = 0;
    let gstTotal = 0;
    items.forEach((row) => {
      const amount = Number(row.qty || 0) * Number(row.rate || 0);
      const gst = (amount * Number(row.gst || 0)) / 100;
      subtotal += amount;
      gstTotal += gst;
    });
    return { subtotal, gstTotal, total: subtotal + gstTotal };
  }, [items]);

  const updateItem = (id, field, value) => {
    setItems((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const addItem = () => setItems((rows) => [...rows, emptyItem()]);

  const removeItem = (id) => {
    if (items.length === 1) {
      toast.error("At least one item is required.");
      return;
    }
    setItems((rows) => rows.filter((row) => row.id !== id));
  };

  const save = (status) => {
    if (!customer.trim()) {
      toast.error("Customer name is required.");
      return;
    }
    if (!items.some((row) => row.item.trim())) {
      toast.error("Add at least one item.");
      return;
    }

    setSaving(true);
    try {
      const existing = safeArray(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
      const payload = {
        id: editData?.id || Date.now(),
        type: "Proforma Invoice",
        piNo,
        date,
        customer,
        customerId,
        customerMobile,
        customerEmail,
        customerAddress,
        notes,
        terms,
        items,
        subtotal: totals.subtotal,
        gstTotal: totals.gstTotal,
        total: totals.total,
        status: status === "draft" ? "Draft" : "Sent",
        sourceRef,
        createdAt: editData?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const next = editData
        ? existing.map((r) => (r.id === editData.id ? payload : r))
        : [...existing, payload];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));

      toast.success(status === "draft" ? "PI saved as draft." : "PI created successfully.");
      setTimeout(() => navigate("/dashboard/proforma-invoices"), 600);
    } catch (error) {
      console.error("PI save failed:", error);
      toast.error("Unable to save PI.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="note-page">
      <div className="note-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />
      <header className="note-header">
        <div className="note-header-left">
          <div className="note-page-icon"><ReceiptLongOutlined /></div>
          <div className="note-page-title-wrap">
            <h1>{editData ? "Edit" : "New"} Proforma Invoice</h1>
            <p>{quotationData ? `Created from ${quotationData.quotationNo || 'Quotation'}` : 'Create a proforma invoice'}</p>
          </div>
        </div>
        <button type="button" className="note-header-back" onClick={() => navigate("/dashboard/proforma-invoices")}>
          <ArrowBack />
        </button>
      </header>

      <main className="note-shell">
        <section className="note-card">
          <div className="note-section-head">
            <h2>PI Details</h2>
          </div>
          <div className="note-form-grid">
            <label className="note-field-group">
              <span>PI Number</span>
              <input className="note-field" value={piNo} onChange={(e) => setPiNo(e.target.value)} />
            </label>
            <label className="note-field-group">
              <span>Date</span>
              <input type="date" className="note-field" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
          </div>
        </section>

        <section className="note-card">
          <div className="note-section-head">
            <h2>Customer Details</h2>
          </div>
          <div className="note-form-grid">
            <label className="note-field-group">
              <span>Customer Name *</span>
              <input className="note-field" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Customer name" />
            </label>
            <label className="note-field-group">
              <span>Mobile</span>
              <input className="note-field" value={customerMobile} onChange={(e) => setCustomerMobile(e.target.value)} placeholder="Mobile number" />
            </label>
            <label className="note-field-group">
              <span>Email</span>
              <input className="note-field" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="Email" />
            </label>
            <label className="note-field-group note-field-wide">
              <span>Address</span>
              <textarea className="note-field note-textarea" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)} placeholder="Address" />
            </label>
          </div>
        </section>

        <section className="note-card">
          <div className="note-section-head">
            <h2>Items</h2>
            <button type="button" className="note-outline-btn" onClick={addItem}>+ Add Item</button>
          </div>
          <div className="note-table-wrap">
            <table className="note-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item / Description</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>GST %</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => {
                  const amount = Number(row.qty || 0) * Number(row.rate || 0);
                  const gstAmount = (amount * Number(row.gst || 0)) / 100;
                  return (
                    <tr key={row.id}>
                      <td>{index + 1}</td>
                      <td>
                        <input className="note-table-input" value={row.item} onChange={(e) => updateItem(row.id, "item", e.target.value)} placeholder="Item name" />
                      </td>
                      <td>
                        <input className="note-table-input note-number" type="number" min="1" value={row.qty} onChange={(e) => updateItem(row.id, "qty", e.target.value)} />
                      </td>
                      <td>
                        <input className="note-table-input note-number" type="number" min="0" step="0.01" value={row.rate} onChange={(e) => updateItem(row.id, "rate", e.target.value)} />
                      </td>
                      <td>
                        <NoteSelect className="note-table-input" value={row.gst} onChange={(v) => updateItem(row.id, "gst", v)} options={[{ value: "0", label: "0%" }, { value: "5", label: "5%" }, { value: "12", label: "12%" }, { value: "18", label: "18%" }, { value: "28", label: "28%" }]} />
                      </td>
                      <td className="note-amount">₹ {money(amount + gstAmount)}</td>
                      <td>
                        <button type="button" className="note-delete-btn" onClick={() => removeItem(row.id)} disabled={items.length === 1}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="note-card">
          <div className="note-summary">
            <div className="note-summary-row">
              <span>Subtotal</span>
              <strong>₹ {money(totals.subtotal)}</strong>
            </div>
            <div className="note-summary-row">
              <span>GST</span>
              <strong>₹ {money(totals.gstTotal)}</strong>
            </div>
            <div className="note-summary-grand">
              <span>Total</span>
              <strong>₹ {money(totals.total)}</strong>
            </div>
          </div>
        </section>

        <section className="note-card">
          <div className="note-form-grid">
            <label className="note-field-group note-field-wide">
              <span>Notes</span>
              <textarea className="note-field note-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
            </label>
            <label className="note-field-group note-field-wide">
              <span>Terms & Conditions</span>
              <textarea className="note-field note-textarea" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms and conditions" />
            </label>
          </div>
        </section>

        <div className="note-actions">
          <button type="button" className="note-btn note-secondary" onClick={() => navigate("/dashboard/proforma-invoices")}>Cancel</button>
          <button type="button" className="note-btn note-secondary" onClick={() => save("draft")} disabled={saving}>{saving ? "Saving..." : "Save as Draft"}</button>
          <button type="button" className="note-btn note-primary" onClick={() => save("sent")} disabled={saving}>{saving ? "Saving..." : "Save PI"}</button>
        </div>
      </main>
    </div>
  );
}
